import {
  NgModule,
} from '@angular/core';
import {
  RouterModule,
  Routes,
} from '@angular/router';
import {
  O3rOnNavigationPreloadingStrategy,
} from '@o3r/routing';
import {
  AutoCheckinLandingRoute,
  AutoCheckinRoutes,
  DirectBoardingPassRoute,
  pageIds,
  PagesTitle,
  SsciRouteParams,
  SsciRoutes,
} from 'src/configuration/routes';
import {
  AddFlightIdParamGuard,
  AddFlightIdParamModule,
} from 'src/guards/add-flight-id-param/index';
import {
  AddServiceCodeParamGuard,
  AddServiceCodeParamModule,
} from 'src/guards/add-service-code-param/index';
import {
  StoresPendingGuard,
  StoresPendingGuardModule,
} from 'src/guards/stores-pending/index';
import {
  CancelCheckInEligibleGuard,
  CancelCheckInEligibleGuardModule,
} from '../guards/cancel-check-in-eligible/index';

const appRoutes: Routes = [
  {
    path: '',
    redirectTo: SsciRoutes.identification,
    pathMatch: 'full'
  },
  {
    path: SsciRoutes.baggage,
    loadChildren: () => import('./ssci/services/baggage/baggage.module').then((m) => m.BaggageModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.baggage,
      id: pageIds.baggage
    }
  },
  {
    path: SsciRoutes.identification,
    loadChildren: () => import('./ssci/identification/identification.module').then((m) => m.IdentificationModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.identification,
      id: pageIds.identification
    }
  },
  {
    path: SsciRoutes.dangerousGoods,
    loadChildren: () => import('./ssci/dangerous-goods/dangerous-goods.module').then((m) => m.DangerousGoodsModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.dangerousGoods,
      id: pageIds.dangerousGoods
    }
  },
  {
    path: SsciRoutes.customerContact,
    loadChildren: () => import('./ssci/customer-contact/customer-contact.module').then((m) => m.CustomerContactModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.customerContact,
      id: pageIds.customerContact
    }
  },
  {
    path: SsciRoutes.passengerSelection,
    loadChildren: () => import('./ssci/passenger-selection/passenger-selection.module').then((m) => m.PassengerSelectionModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.passengerSelection,
      id: pageIds.passengerSelection
    }
  },
  {
    path: SsciRoutes.passengerSelectionContact,
    loadChildren: () => import('./ssci/passenger-selection-contact/passenger-selection-contact.module').then((m) => m.PassengerSelectionContactModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.passengerSelectionContact,
      id: pageIds.passengerSelectionContact
    }
  },
  {
    path: SsciRoutes.boardingPass,
    loadChildren: () => import('./ssci/boarding-pass/boarding-pass.module').then((m) => m.BoardingPassModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.boardingPass,
      id: pageIds.boardingPass
    }
  },
  {
    path: SsciRoutes.boardingPassDelivery,
    loadChildren: () => import('./ssci/boarding-pass-delivery/boarding-pass-delivery.module').then((m) => m.BoardingPassDeliveryModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.boardingPassDelivery,
      id: pageIds.boardingPassDelivery
    }
  },
  {
    path: SsciRoutes.payment,
    loadChildren: () => import('./ssci/payment/payment.module').then((m) => m.PaymentModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.payment,
      id: pageIds.payment
    }
  },
  {
    path: SsciRoutes.securityQuestions,
    loadChildren: () => import('./ssci/security-questions/security-questions.module').then((m) => m.SecurityQuestionsModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.securityQuestions,
      id: pageIds.securityQuestions
    }
  },
  {
    path: SsciRoutes.seatmap,
    loadChildren: () => import('./ssci/services/seatmap/seatmap.module').then((m) => m.SeatmapModule),
    canActivate: [AddFlightIdParamGuard],
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.seatmap,
      id: pageIds.seatmap
    }
  },
  {
    path: SsciRoutes.journeySelection,
    loadChildren: () => import('./ssci/journey-selection/journey-selection.module').then((m) => m.JourneySelectionModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.journeySelection,
      id: pageIds.journeySelection
    }
  },
  {
    path: SsciRoutes.overview,
    loadChildren: () => import('./ssci/overview/overview.module').then((m) => m.OverviewModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.overview,
      id: pageIds.overview
    }
  },
  {
    path: `${SsciRoutes.regulatoryDetails}/:${SsciRouteParams.journeyId}/:${SsciRouteParams.travelerId}`,
    loadChildren: () => import('./ssci/regulatory-details/regulatory-details.module').then((m) => m.RegulatoryDetailsModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.regulatoryDetails,
      id: pageIds.regulatoryDetails
    }
  },
  {
    path: SsciRoutes.challenge,
    loadChildren: () => import('./ssci/challenge/challenge.module').then((m) => m.ChallengeModule),
    data: {
      title: PagesTitle.challenge,
      id: pageIds.challenge
    }
  },
  {
    path: SsciRoutes.checkinError,
    loadChildren: () => import('./ssci/checkin-error/checkin-error.module').then((m) => m.CheckinErrorModule),
    data: {
      title: PagesTitle.checkinError,
      id: pageIds.checkinError
    }
  },
  {
    path: SsciRoutes.catalog,
    loadChildren: () => import('./ssci/catalog/catalog.module').then((m) => m.CatalogModule),
    data: {
      title: PagesTitle.catalog,
      id: pageIds.catalog
    }
  },
  {
    path: SsciRoutes.service,
    loadChildren: () => import('./ssci/services/extra-services/extra-services.module').then((m) => m.ExtraServicesModule),
    canActivate: [AddServiceCodeParamGuard],
    data: {
      title: PagesTitle.services,
      id: pageIds.service
    }
  },
  {
    path: SsciRoutes.homePrintedBagTags,
    loadChildren: () => import('./ssci/home-printed-bag-tags/home-printed-bag-tags.module').then((m) => m.HomePrintedBagTagsModule),
    data: {
      title: PagesTitle.homePrintedBagTags,
      id: pageIds.homePrintedBagTags
    }
  },
  {
    path: SsciRoutes.cancel,
    canActivate: [CancelCheckInEligibleGuard],
    loadChildren: () => import('./ssci/cancelation/cancelation.module').then((m) => m.CancelationModule)
  },
  {
    path: AutoCheckinLandingRoute.landing,
    redirectTo: AutoCheckinRoutes.autoCheckinDangerousGoods
  },
  {
    path: AutoCheckinRoutes.autoCheckinError,
    loadChildren: () => import('./ssci/auto-checkin/auto-checkin-error/auto-checkin-error-pres.module').then((m) => m.AutoCheckinErrorPresModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.autoCheckinError,
      id: pageIds.autoCheckinError
    }
  },
  {
    path: AutoCheckinRoutes.autoCheckinDangerousGoods,
    loadChildren: () => import('./ssci/auto-checkin/auto-checkin-dangerous-goods/auto-checkin-dangerous-goods.module').then((m) => m.AutoCheckinDangerousGoodsModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.autoCheckinDangerousGoods,
      id: pageIds.autoCheckinDangerousGoods
    }
  },
  {
    path: AutoCheckinRoutes.autoCheckinBoardingPass,
    loadChildren: () => import('./ssci/auto-checkin/auto-checkin-boarding-pass/auto-checkin-boarding-pass.module').then((m) => m.AutoCheckinBoardingPassModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.autoCheckinBoardingPass,
      id: pageIds.autoCheckinBoardingPass
    }
  },
  {
    path: DirectBoardingPassRoute.landing,
    loadChildren: () => import('./ssci/bp/bp.module').then((m) => m.BpModule),
    canDeactivate: [StoresPendingGuard]
  },
  {
    path: `${SsciRoutes.travelerIdentityWidgetPassport}/:${SsciRouteParams.journeyId}/:${SsciRouteParams.travelerId}/:${SsciRouteParams.travelerIdentityWidgetType}`,
    loadChildren: () => import('./ssci/traveler-identity-widget/traveler-identity-widget.module').then((m) => m.TravelerIdentityWidgetModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.travelerIdentityWidgetPassport,
      id: pageIds.travelerIdentityWidgetPassport
    }
  },
  {
    path: `${SsciRoutes.travelerIdentityWidgetEntryVerifier}/:${SsciRouteParams.journeyId}/:${SsciRouteParams.travelerId}/:${SsciRouteParams.travelerIdentityWidgetType}`,
    loadChildren: () => import('./ssci/traveler-identity-widget/traveler-identity-widget.module').then((m) => m.TravelerIdentityWidgetModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.travelerIdentityWidgetEntryVerifier,
      id: pageIds.travelerIdentityWidgetEntryVerifier
    }
  },
  {
    path: SsciRoutes.reviewCheckin,
    loadChildren: () => import('./ssci/review-checkin/review-checkin.module').then((m) => m.ReviewCheckinModule),
    data: {
      title: PagesTitle.reviewCheckin,
      id: pageIds.reviewCheckin
    }
  },
  {
    path: SsciRoutes.mandate,
    loadChildren: () => import('./ssci/mandate/mandate.module').then((m) => m.MandateModule),
    data: {
      title: PagesTitle.mandate,
      id: pageIds.mandate
    }
  },
  {
    path: SsciRoutes.exitRowEligibility,
    loadComponent: () => import('./ssci/exit-row-eligibility/exit-row-eligibility.component').then((m) => m.ExitRowEligibilityComponent),
    data: {
      title: PagesTitle.exitRowEligibility,
      id: pageIds.exitRowEligibility
    }
  },
  {
    path: SsciRoutes.voluntaryDeniedBoarding,
    loadChildren: () => import('./ssci/voluntary-denied-boarding/voluntary-denied-boarding.module').then((m) => m.VoluntaryDeniedBoardingModule),
    data: {
      title: PagesTitle.voluntaryDeniedBoarding,
      id: pageIds.voluntaryDeniedBoarding
    }
  },
  {
    path: SsciRoutes.alternativeFlights,
    loadChildren: () => import('./ssci/alternative-flights/alternative-flights.module').then((m) => m.AlternativeFlightsModule),
    canDeactivate: [StoresPendingGuard],
    data: {
      title: PagesTitle.alternativeFlights,
      id: pageIds.alternativeFlights
    }
  },
  {
    path: '**',
    redirectTo: SsciRoutes.identification,
    pathMatch: 'full'
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      preloadingStrategy: O3rOnNavigationPreloadingStrategy,
      scrollPositionRestoration: 'top'
    }),
    AddFlightIdParamModule,
    AddServiceCodeParamModule,
    CancelCheckInEligibleGuardModule,
    StoresPendingGuardModule
  ],
  providers: [O3rOnNavigationPreloadingStrategy],
  exports: [RouterModule]
})
export class AppRoutingModule {}
